import { callAPI } from "../utils/utils";
import { useSelector } from "react-redux";
import { useEffect, useRef } from "react";
import structure from "../utils/structure";
import { useGLTF } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { DoubleSide, MeshPhysicalMaterial } from "three";
import React, { useMemo, useState, forwardRef } from "react";

const Suite = forwardRef((props, ref) => {
    const { nodes, materials } = useGLTF(props.model);
    const glassMaterial = useMemo(
        () =>
            new MeshPhysicalMaterial({
                side: DoubleSide,
                color: "#E0FFFF",
                transmission: 0.99,
                thickness: 0.05,
                roughness: 0.1,
                metalness: 0.25,
            })
    );

    const model = useSelector((state) => state.roomData.model);
    const roof = useSelector((state) => state.roomData.roof);
    const exterior = useSelector((state) => state.roomData.exterior);
    const interior = useSelector((state) => state.roomData.interior);
    const extras = useSelector((state) => state.roomData.extras);
    const extAccent = useSelector((state) => state.roomData.extAccent);
    const intAccent = useSelector((state) => state.roomData.intAccent);
    const visibleRoof = useSelector((state) => state.roomData.visibleRoof);

    const roofsRef = useRef();
    const bedRef = useRef();
    const armarioRef = useRef();
    const materialsRef = useRef();
    const floorRef = useRef();
    const extAccentsRef = useRef();
    const intAccentRef = useRef();
    const lampsRef = useRef();

    const [tilted, setTilted] = useState(false);
    const [panelState, setPanelState] = useState(0); // 0 is hidden, 1 is flat and 2 is tilted

    const { gl } = useThree();

    Object.entries(materials).forEach((material) => {
        if (material[1].map) {
            material[1].map.anisotropy = gl.capabilities.getMaxAnisotropy();
        }
    });

    function updateExtras() {
        bedRef.current.visible = false;
        armarioRef.current.visible = false;
        lampsRef.current.visible = false;
        setPanelState(0);
        for (let i = 0; i < extras.length; i++) {
            if (extras[i][0] == 0) { bedRef.current.visible = true; }
            if (extras[i][0] == 1) { armarioRef.current.visible = true; }
            if (extras[i][0] == 2) { setPanelState(tilted ? 2 : 1); }
            if (extras[i][0] == 4) { lampsRef.current.visible = true; }
        }
    }

    function updateModel() {
        const currentRoof = structure[1].meshNames[structure[1].elements.indexOf(roof)];
        const currentExterior = structure[2].meshNames[structure[2].elements.indexOf(exterior)];

        const tilt = structure[1].elements[structure[1].elements.indexOf(roof)].includes("Dos Aguas");

        for (var i in roofsRef.current.children) {
            if (roofsRef.current.children[i].name == currentRoof) {
                roofsRef.current.children[i].visible = true;
            } else {
                roofsRef.current.children[i].visible = false;
            }
        }

        floorRef.current.material = materials[structure[3].materials[structure[3].elements.indexOf(interior)]];
        switch (intAccent){
            case "Ninguno":
                intAccentRef.current.material = floorRef.current.material;
                break;
            case "Porcelanico Gris":
                intAccentRef.current.material = materials["floor-gris"];
                break;
            case "Porcelanico Antracita":
                intAccentRef.current.material = materials["floor-antracita"];
                break;
            case "Porcelanico Arena":
                intAccentRef.current.material = materials["floor-arena"];
                break;
        }

        for (var i in materialsRef.current.children) {
            if ( materialsRef.current.children[i].name == currentExterior || (tilt && materialsRef.current.children[i].name == currentExterior + "-tejado")) {
                materialsRef.current.children[i].visible = true;
                materialsRef.current.children[i].material = materials[structure[2].materials[structure[2].elements.indexOf(exterior)]];

                if (extAccentsRef.current.children[i]) {
                    if (extAccent == "Ninguno") {
                        extAccentsRef.current.children[i].visible = true;
                        extAccentsRef.current.children[i].material =
                            materials[structure[2].materials[structure[2].elements.indexOf(exterior)]];
                    } else {
                        extAccentsRef.current.children[0].visible = true;
                        extAccentsRef.current.children[1].visible = false;
                        extAccentsRef.current.children[2].visible = false;
                        extAccentsRef.current.children[0].material = materials["wood-baked"];
                    }
                }
            } else {
                materialsRef.current.children[i].visible = false;
                if (extAccentsRef.current.children[i]) {
                    if (extAccent == "Ninguno") {
                        extAccentsRef.current.children[i].visible = false;
                    }
                }
            }
        }
        setTilted(tilt);
    }

    useEffect(() => { callAPI(); }, [model, roof, exterior, interior, extAccent, intAccent, extras]);
    useEffect(() => { updateModel(); }, [roof, exterior, interior, extAccent, intAccent]);
    useEffect(() => { if (nodes) { updateModel(); } }, [nodes]);
    useEffect(() => { updateExtras(); }, [extras, tilted]);

    return (
        <group position={[0, -0.6, 0]} scale={0.4} rotation={[0, Math.PI, 0]} dispose={null}>
            {/* Estructura base del edificio */}
            <mesh name="edificio-joined_1" geometry={nodes["edificio-joined_1"].geometry} material={materials.baked} />
            <mesh name="edificio-joined_2" geometry={nodes["edificio-joined_2"].geometry} material={materials["floor-eyre"]} ref={ floorRef } />
            <mesh name="edificio-joined_3" geometry={nodes["edificio-joined_3"].geometry} material={materials["floor-eyre"]} ref={ intAccentRef } />
            <mesh name="edificio-joined_4" geometry={nodes["edificio-joined_4"].geometry} material={glassMaterial} />

            {/* Tejados */}
            <group ref={roofsRef} visible={visibleRoof}>
                <mesh name="techo-grava" geometry={nodes["techo-grava"].geometry} material={materials["flat-roofs-baked"]} />
                <mesh name="techo-hierba" geometry={nodes["techo-hierba"].geometry} material={materials["flat-roofs-baked"]} />
                <mesh name="techo-chapa" geometry={nodes["techo-chapa"].geometry} material={materials["roof-baked"]} />
                <mesh name="techo-teja" geometry={nodes["techo-teja"].geometry} material={materials["roof-baked"]} />
                <mesh name="techo-chapa-roja" geometry={nodes["techo-chapa-roja"].geometry} material={materials["roof-baked"]} />
            </group>

            {/* Acabados */}
            <group ref={materialsRef}>
                <mesh name="acabado-madera" geometry={nodes["acabado-madera"].geometry} material={materials["wood-baked"]} />
                <mesh name="acabado-porcelanico" geometry={nodes["acabado-porcelanico"].geometry} material={materials["antracita-baked"]} />
                <mesh name="acabado-chapa" geometry={nodes["acabado-chapa"].geometry} material={materials["grey-metal-baked"]} />

                <mesh name="acabado-madera-tejado" geometry={nodes["acabado-madera-tejado"].geometry} material={materials["wood-baked"]} />
                <mesh name="acabado-porcelanico-tejado" geometry={nodes["acabado-porcelanico-tejado"].geometry} material={materials["antracita-baked"]} />
                <mesh name="acabado-chapa-tejado" geometry={nodes["acabado-chapa-tejado"].geometry} material={materials["grey-metal-baked"]} />
            </group>

            {/* Acentos */}
            <group ref={extAccentsRef}>
                <mesh name="decoracion-madera" geometry={nodes["decoracion-madera"].geometry} material={materials["wood-baked"]} />
                <mesh name="decoracion-porcelanico" geometry={nodes["decoracion-porcelanico"].geometry} material={materials["antracita-baked"]} />
                <mesh name="decoracion-chapa" geometry={nodes["decoracion-chapa"].geometry} material={materials["grey-metal-baked"]} />
            </group>

            {/* Extras */}
            <mesh ref={lampsRef} visible={false} name="lamps" geometry={nodes["lamps"].geometry} material={materials["lamp"]} />
            <mesh ref={bedRef} visible={false} name="bedroom" geometry={nodes["bedroom"].geometry} material={materials["bed-baked"]} />
            <mesh ref={armarioRef} visible={false} name="armario" geometry={nodes["armario"].geometry} material={materials["armario"]} />
            <mesh visible={visibleRoof && panelState == 1} name="panel-flat" geometry={nodes["panel-flat"].geometry} material={materials["panel"]} />
            <mesh visible={visibleRoof && panelState == 2} name="panels-angle" geometry={nodes["panels-angle"].geometry} material={materials["panel"]} />
        </group>
    );
});

export default Suite;
